import React, { useEffect, useState } from 'react';
import { FaBuilding, FaDoorOpen, FaChair, FaUsers, FaFileInvoice, FaCreditCard, FaRocket, FaCheck, FaArrowRight } from 'react-icons/fa';
import { FiStar } from 'react-icons/fi';
import { 
  getLibraries, 
  getSeatsData, 
  getStudents,
  getPaymentPlans,
  getDashboardData 
} from '../services/api';
import { DEMO_LOGIN_ENABLED } from '../demoMode.js';
import '../styles/pages/setupguide.css';
export function SetupGuide({ setPage }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    libraries: 0,
    rooms: 0,
    seats: 0,
    students: 0,
    plans: 0,
    hasQR: false
  });

  useEffect(() => {
    const load = async () => {
      if (DEMO_LOGIN_ENABLED) {
        setStats({ libraries: 2, rooms: 3, seats: 36, students: 4, plans: 3, hasQR: false });
        setLoading(false);
        return;
      }
      try {
        const [libs, roomsData, studentsRes, plansRes, dash] = await Promise.all([
          getLibraries().catch(() => []),
          getSeatsData().catch(() => ({ rooms: [], seats: [] })),
          getStudents().catch(() => []),
          getPaymentPlans().catch(() => []),
          getDashboardData().catch(() => ({ dashboard: {} }))
        ]);

        setStats({
          libraries: libs?.length || 0,
          rooms: roomsData.rooms?.length || 0,
          seats: roomsData.seats?.length || 0,
          students: Array.isArray(studentsRes) ? studentsRes.length : studentsRes?.length || 0,
          plans: plansRes?.length || 0,
          hasQR: !!dash?.dashboard?.payment_qr || !!localStorage.getItem('payment_qr')
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const steps = [
    { title: 'Create Library', desc: 'Set up your first library branch with location and basic details.', done: stats.libraries > 0, dest: 'Libraries' },
    { title: 'Add Rooms', desc: 'Create rooms in your library and set their capacity.', done: stats.rooms > 0, dest: 'Seats' },
    { title: 'Configure Seats', desc: 'Set up seats in each room and manage their availability.', done: stats.seats > 0, dest: 'Seats' },
    { title: 'Add Students', desc: 'Enroll students and assign them to rooms and payment plans.', done: stats.students > 0, dest: 'Students' },
    { title: 'Create Payment Plans', desc: 'Set up subscription plans with amounts and durations.', done: stats.plans > 0, dest: 'Payment Plans' },
    { title: 'Configure Payment & QR', desc: 'Upload your payment QR code and configure payment settings.', done: stats.hasQR, dest: 'Payments' },
    { title: 'Go Live', desc: 'Complete setup and start managing your library.', done: stats.libraries > 0 && stats.rooms > 0 && stats.seats > 0 && stats.students > 0 && stats.plans > 0 && stats.hasQR, dest: 'Dashboard' }
  ];

  const completed = steps.filter(s => s.done).length;
  const total = steps.length;
  const percent = Math.round((completed / total) * 100);

  const nextStep = steps.findIndex(step => !step.done);
  const getStatus = (step, idx) => {
    if (step.done) return 'Completed';
    if (idx === nextStep) return 'In Progress';
    return 'Not Started';
  };

  const getIcon = (title) => {
    if (title.includes('Library')) return FaBuilding;
    if (title.includes('Rooms')) return FaDoorOpen;
    if (title.includes('Seats')) return FaChair;
    if (title.includes('Students')) return FaUsers;
    if (title.includes('Payment Plans')) return FaFileInvoice;
    if (title.includes('Payment & QR')) return FaCreditCard;
    return FaRocket;
  };
  return (
    <div className="setup-page">
      <header className="setup-intro profile-top-header">
        <div className="profile-heading">
          <span className="setup-intro-mark profile-heading-icon" aria-hidden="true"><FiStar /></span>
          <div className="profile-heading-copy">
            <span className="setup-eyebrow">YOUR WORKSPACE CHECKLIST</span>
            <h1>Set up your study space</h1>
            <p>Follow these steps to get your library ready for students.</p>
          </div>
        </div>
        <div className="profile-header-actions">
          <span className="setup-header-progress">{loading ? 'Loading progress' : `${completed} of ${total} complete`}</span>
        </div>
      </header>

      {loading ? (
        <div className="setup-skeleton" role="status" aria-label="Loading setup guide">
          <section className="panel setup-progress setup-progress-skeleton" aria-hidden="true">
            <div className="setup-skeleton-copy"><span /><b /><i /></div>
            <div className="setup-skeleton-percent" />
            <div className="setup-skeleton-track" />
          </section>
          <div className="setup-section-heading setup-skeleton-section" aria-hidden="true"><span /><i /></div>
          <div className="setup-list" aria-hidden="true">
            {Array.from({ length: 6 }, (_, index) => (
              <article className="panel setup-card setup-card-skeleton" key={index}>
                <span className="setup-skeleton-icon" />
                <div className="setup-skeleton-card-copy"><i /><b /><span /></div>
                <span className="setup-skeleton-button" />
              </article>
            ))}
          </div>
          <span className="setup-sr-only">Loading setup progressÃ¢â‚¬Â¦</span>
        </div>
      ) : (
        <>
          <div className="panel setup-progress">
            <div className="setup-progress-top">
              <div>
                <span className="setup-eyebrow">SETUP PROGRESS</span>
                <h3>{completed} <span>/ {total} steps complete</span></h3>
                <small>{completed === total ? 'Your workspace is ready to go.' : 'You are making great progress. Continue with the next step.'}</small>
              </div>
              <div className="setup-progress-percent"><b>{percent}%</b><span>complete</span></div>
            </div>
            <div className="setup-progress-track">
              <div className="setup-progress-fill" style={{ width: `${percent}%` }} />
            </div>
          </div>

          <div className="setup-section-heading">
            <div><h3>Setup checklist</h3><p>Complete the steps in order, or jump to any section.</p></div>
            <span>{completed} / {total} complete</span>
          </div>
          <div className="setup-list">
            {steps.map((step, i) => {
              const status = getStatus(step, i);
              return (
                <article key={step.title} className={`panel setup-card ${status === 'In Progress' ? 'current' : ''}`}>
                  <div className={`setup-icon ${status === 'Completed' ? 'icon-done' : status === 'In Progress' ? 'icon-progress' : 'icon-todo'}`} aria-hidden="true">
                    {status === 'Completed' ? <FaCheck /> : React.createElement(getIcon(step.title))}
                  </div>
                  <div className="setup-content">
                    <div className="setup-step-row">
                      <span>Step {i + 1}</span>
                      <b className={`setup-badge ${status === 'Completed' ? 'badge-done' : status === 'In Progress' ? 'badge-progress' : ''}`}>{status}</b>
                    </div>
                    <h2>{step.title}</h2>
                    <p>{step.desc}</p>
                  </div>
                  <span className="setup-action">
                    {status === 'Completed' ? (
                      <button type="button" className="btn-view" onClick={() => setPage(step.dest)}>View <FaArrowRight aria-hidden="true" /></button>
                    ) : (
                      <button type="button" className="btn-start" onClick={() => setPage(step.dest)}>Start <FaArrowRight aria-hidden="true" /></button>
                    )}
                  </span>
                </article>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
